/**
 * client/src/ui/PageHead.tsx — the one page header. Every screen's opening band comes from here.
 *
 * WHAT WAS WRONG  (owner, 2026-08-31: "the consistency across the entire website ... it needs to be
 * easy to navigate from area to area", and on the comparable-sales screen: "above the cards or
 * table view, there is a lot of open space. everything is crowded left").
 *
 * MEASURED, not felt. Twenty-five `<h1>`s outside the tests, opened by hand, one screen at a time:
 * three different sizes (22, 24, and the CRM's 17), four different bottom margins (0, 4, 6, 8), a
 * subtitle that is sometimes a `<p style={{fontSize:14}}>` and sometimes a 13, and a back link that
 * is a different construction on each of the three screens that has one. Nobody chose any of that.
 * Each screen chose, and the result is a product that reads as a set of pages built by different
 * people — which is exactly what "consistency across the entire website" is describing.
 *
 * THE ACTIONS SLOT IS THE OTHER HALF OF THE SAME PROBLEM, and it is the one that produced the
 * screenshot. A screen's controls were laid out with `justify-content: space-between` against the
 * PAGE, which since the full-bleed change is the whole glass: on the comparable-sales screen at a
 * 1920 viewport that put the Cards/Table switch roughly 1700px from the heading it belongs to. A
 * control that far from its subject does not read as belonging to it. Here the actions are a child
 * of the header, so they sit at the right END OF THE HEADER, on the heading's own baseline, however
 * wide the page is. That is the whole trick, and it is why this is a component and not a CSS class:
 * the arrangement has to be impossible to get wrong, not merely documented somewhere.
 *
 * WHAT THIS DELIBERATELY IS NOT:
 *
 *   * NOT a width. The page's measure is `AppShell`'s job and the block's measure is `Block`'s.
 *     A header that also picked a width would be a third opinion about the same number.
 *   * NOT navigation. `back` is a single link to the place this screen was opened from, which is a
 *     property of THIS screen. The app's actual navigation lives in `AppShell`.
 *   * NOT a place for a status banner. A sandbox notice, a licence note or a "disclosed, not
 *     relied" band is content and belongs in the page under the header, at full size. Tucking a
 *     disclosure into a header's small grey type is how honest products stop looking honest.
 */
import type { CSSProperties, ReactNode } from "react";

import { themeVar as colors } from "../design-tokens";

export interface PageHeadProps {
  /** The screen's name. Rendered as the `<h1>` unless `as` says otherwise. */
  title: ReactNode;
  /**
   * The small uppercase line above the title — a step counter, a section name, a record type.
   * Kept SHORT on purpose: it is a locator, not a sentence.
   */
  eyebrow?: ReactNode;
  /** One or two lines under the title saying what this screen is for. Capped at a reading measure. */
  sub?: ReactNode;
  /** Controls that act on the whole screen. Right end of the header, on the title's baseline. */
  actions?: ReactNode;
  /** A single "back to where I came from" link, above the eyebrow. */
  back?: { label: ReactNode; onClick: () => void };
  /**
   * `h1` for a screen, `h2` for a major section inside one. The type scale does not change with the
   * level — a section heading that looked like a page heading was one of the things being reported
   * as inconsistent — only the document outline does.
   */
  as?: "h1" | "h2";
  /** A hairline under the header. On by default: it is what makes the content below read as
   *  belonging to it rather than as the next unrelated strip. */
  rule?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
}

export function PageHead({
  title, eyebrow, sub, actions, back, as: Tag = "h1", rule = true, style, children,
}: PageHeadProps) {
  return (
    <header
      className="vb-pagehead"
      data-rule={rule ? "on" : undefined}
      style={style}
    >
      {back && (
        <button type="button" className="vb-pagehead-back" onClick={back.onClick}>
          <span aria-hidden="true">←</span>
          {back.label}
        </button>
      )}
      <div className="vb-pagehead-row">
        <div className="vb-pagehead-titles">
          {eyebrow && <p className="vb-pagehead-eyebrow">{eyebrow}</p>}
          <Tag className="vb-pagehead-title">{title}</Tag>
          {sub && <p className="vb-pagehead-sub">{sub}</p>}
        </div>
        {/* `min-width: 0` on the titles and `flex: none` here is what stops a long address in the
            title from squashing the controls into an unreadable column. The title wraps; the
            buttons do not. */}
        {actions && <div className="vb-pagehead-actions">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/**
 * The colour a `PageHead` subtitle uses, exported for the handful of places that render their own
 * supporting line beside one and must match it rather than re-pick a grey.
 */
export const PAGEHEAD_SUB_COLOR = colors.textSecondary;
